import { FC, useState, FormEvent } from 'react'
import { Button } from '../../../ui/Button'
import { X } from 'lucide-react'
import { guarantorService } from '../../../../services/guarantorService'
import toast from 'react-hot-toast'

interface GuarantorInviteModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  creditApplicationId?: string
}

const GuarantorInviteModal: FC<GuarantorInviteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  creditApplicationId,
}) => {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [rfc, setRfc] = useState('')
  const [personType, setPersonType] = useState<'PM' | 'PFAE'>('PM')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    fullName?: string
    email?: string
    phone?: string
    rfc?: string
  }>({})

  if (!isOpen) return null

  const validateForm = () => {
    const newErrors: typeof errors = {}
    let isValid = true

    // Validar Nombre/Empresa (mínimo 3 caracteres)
    if (!fullName.trim() || fullName.trim().length < 3) {
      newErrors.fullName =
        personType === 'PM'
          ? 'el nombre de la empresa debe tener al menos 3 caracteres'
          : 'El nombre debe tener al menos 3 caracteres'
      isValid = false
    } else if (
      personType === 'PFAE' &&
      !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(fullName)
    ) {
      newErrors.fullName = 'El nombre solo debe contener letras.'
      isValid = false
    }

    // Validar RFC (Persona Física o Moral)
    const rfcMoralRegex =
      /^[A-ZÑ&]{3}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/i
    const rfcFisicaRegex =
      /^[A-ZÑ&]{4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/i

    if (!rfc.trim()) {
      newErrors.rfc = 'El RFC es requerido'
      isValid = false
    } else {
      const rfcValue = rfc.trim().toUpperCase()
      if (personType === 'PM') {
        if (rfcValue.length !== 12) {
          newErrors.rfc = 'El RFC para Persona Moral debe tener 12 caracteres'
          isValid = false
        } else if (!rfcMoralRegex.test(rfcValue)) {
          newErrors.rfc = 'Formato de RFC de Persona Moral no válido'
          isValid = false
        }
      } else if (personType === 'PFAE') {
        if (rfcValue.length !== 13) {
          newErrors.rfc = 'El RFC para Persona Física debe tener 13 caracteres'
          isValid = false
        } else if (!rfcFisicaRegex.test(rfcValue)) {
          newErrors.rfc = 'Formato de RFC de Persona Física no válido'
          isValid = false
        }
      }
    }

    // Validar Correo Electrónico
    if (!email.trim()) {
      newErrors.email = 'El correo electrónico es requerido'
      isValid = false
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Ingresa un formato de correo válido'
      isValid = false
    }

    // Validar Teléfono (10 dígitos numéricos)
    if (!phone.trim()) {
      newErrors.phone = 'El teléfono es requerido'
      isValid = false
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = 'El teléfono debe contener exactamente 10 dígitos'
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    if (!creditApplicationId) {
      toast.error('No se encontró el ID de la solicitud de crédito')
      return
    }

    setLoading(true)
    try {
      const response = await guarantorService.inviteGuarantor(
        creditApplicationId,
        {
          email,
          fullName,
          phone,
          rfc: rfc.toUpperCase(),
          personType,
        }
      )

      if (response?.success) {
        toast.success(
          response.data?.message || 'Invitación enviada exitosamente al aval'
        )
        onSuccess()
        onClose()
      } else {
        toast.error('Ocurrió un error al enviar la invitación')
      }
    } catch (error: unknown) {
      console.error('Error inviting guarantor:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as any)?.response?.data?.message ||
            'Error al enviar la invitación'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Invitar Aval</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            Ingresa los datos del aval para enviarle una invitación.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Persona
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              value={personType}
              onChange={e => setPersonType(e.target.value as 'PM' | 'PFAE')}
            >
              <option value="PM">Persona Moral (PM)</option>
              <option value="PFAE">
                Persona Física con Actividad Empresarial (PFAE)
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {personType === 'PM' ? 'Nombre de la Empresa' : 'Nombre Completo'}
            </label>
            <input
              type="text"
              required
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.fullName
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={fullName}
              onChange={e => {
                setFullName(e.target.value)
                if (errors.fullName)
                  setErrors({ ...errors, fullName: undefined })
              }}
              placeholder={
                personType === 'PM'
                  ? 'Ej. Empresa S.A. de C.V.'
                  : 'Ej. Juan Pérez García'
              }
            />
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RFC
            </label>
            <input
              type="text"
              required
              maxLength={13}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.rfc
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={rfc}
              onChange={e => {
                setRfc(e.target.value.toUpperCase())
                if (errors.rfc) setErrors({ ...errors, rfc: undefined })
              }}
              placeholder="Ej. GARM800315HH8"
            />
            {errors.rfc && (
              <p className="text-red-500 text-xs mt-1">{errors.rfc}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correo Electrónico
            </label>
            <input
              type="email"
              required
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.email
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={email}
              onChange={e => {
                setEmail(e.target.value)
                if (errors.email) setErrors({ ...errors, email: undefined })
              }}
              placeholder="Ej. correo@ejemplo.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              type="tel"
              required
              maxLength={10}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.phone
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={phone}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '') // Solo permitir números
                setPhone(val)
                if (errors.phone) setErrors({ ...errors, phone: undefined })
              }}
              placeholder="Ej. 5512345678"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="px-4 py-2"
            >
              {loading ? 'Enviando...' : 'Enviar Invitación'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GuarantorInviteModal
